import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { CalendarView, calendarViews } from './calendarDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CalendarToolbarProps {
  view: CalendarView;
  /** "October 2026" or "19 – 25 Oct 2026". */
  title: string;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onView: (view: CalendarView) => void;
}

/** Previous, Today, Next, the period and the three views (design a1). */
const CalendarToolbarComponent: React.FC<CalendarToolbarProps> = ({
  view,
  title,
  onPrevious,
  onNext,
  onToday,
  onView,
}) => {
  const intl = useIntl();
  const isWeek = view === 'week';
  const viewNames: Record<CalendarView, string> = {
    month: intl.formatMessage({ id: 'app.sml.promote.calendar.view.month', defaultMessage: 'Month' }),
    week: intl.formatMessage({ id: 'app.sml.promote.calendar.view.week', defaultMessage: 'Week' }),
    list: intl.formatMessage({ id: 'app.sml.promote.calendar.view.list', defaultMessage: 'List' }),
  };

  return (
    <div className={styles.ctool}>
      <button
        type="button"
        className={styles.navbtn}
        aria-label={
          isWeek
            ? intl.formatMessage({ id: 'app.sml.promote.calendar.previousWeek', defaultMessage: 'Previous week' })
            : intl.formatMessage({ id: 'app.sml.promote.calendar.previousMonth', defaultMessage: 'Previous month' })
        }
        onClick={onPrevious}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <button type="button" className={styles.todayBtn} onClick={onToday}>
        {intl.formatMessage({ id: 'app.sml.promote.calendar.today', defaultMessage: 'Today' })}
      </button>
      <button
        type="button"
        className={styles.navbtn}
        aria-label={
          isWeek
            ? intl.formatMessage({ id: 'app.sml.promote.calendar.nextWeek', defaultMessage: 'Next week' })
            : intl.formatMessage({ id: 'app.sml.promote.calendar.nextMonth', defaultMessage: 'Next month' })
        }
        onClick={onNext}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
      <h2 className={styles.mtitle} aria-live="polite">
        {title}
      </h2>
      <span className={styles.grow} />
      <div
        className={styles.seg}
        role="group"
        aria-label={intl.formatMessage({ id: 'app.sml.promote.calendar.view.label', defaultMessage: 'View' })}
      >
        {calendarViews.map((one) => (
          <button
            key={one}
            type="button"
            className={clsx(one === view && styles.segOn)}
            aria-pressed={one === view}
            onClick={() => onView(one)}
          >
            {viewNames[one]}
          </button>
        ))}
      </div>
    </div>
  );
};

export const CalendarToolbar = TypedMemo(CalendarToolbarComponent);
